import { autocompletion, Completion, CompletionContext } from '@codemirror/autocomplete'
import { DeleteRounded, InfoRounded } from '@mui/icons-material'
import { Button, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material'
import { ChangeEvent, memo, ReactNode, useEffect } from 'react'
import { IFlow, INode } from '../../../../definitions/Nodes'
import { IAcceptedData } from '../../../../definitions/NodesDescription'
import NodeManager from '../../../../managers/NodeManager'
import NodeCodemirrorTextEditor from '../NodeCodemirrorTextEditor'

interface P {
    acceptedData: IAcceptedData<'xfrm_operations'>
    selectedNode: INode<'xfrm_operations'>
    flow: IFlow
    setSelected: (node: INode<'xfrm_operations'>) => void
    disable: boolean
}

const NodeOperationsEditor = ({ selectedNode, setSelected, disable, flow }: P) => {
    useEffect(() => {
        if (!selectedNode.data?.operationsData) {
            setSelected({
                ...selectedNode,
                data: {
                    ...selectedNode.data,
                    operationsData: {
                        ...selectedNode.data.operationsData,
                        expressions: [{ expression: '', output: '' }]
                    }
                }
            })
        }
    }, [])

    const handleInputChange = (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement> | string, index: number) => {
        const name = typeof event === 'string' ? 'expression' : event.target.name
        let value = typeof event === 'string' ? event : event.target.value
        if (name === 'output') value = value.trim()

        setSelected({
            ...selectedNode,
            data: {
                ...selectedNode.data,
                operationsData: {
                    ...selectedNode.data.operationsData,
                    expressions: selectedNode.data.operationsData.expressions.map((expr, i) => (i === index ? { ...expr, [name]: value } : expr))
                }
            }
        })
    }

    const renderInfoField = (title: string | ReactNode) => (
        <Tooltip arrow title={title} disableHoverListener={disable}>
            <InfoRounded fontSize='inherit' sx={{ cursor: disable ? undefined : 'pointer', color: disable ? 'var(--icon-disabled)' : 'var(--icon-color)' }} />
        </Tooltip>
    )

    const shapes = NodeManager.getInputShapes<'xfrm_operations'>(selectedNode, flow.nodes)
    const globalsShape = flow?.globals ? Object.keys(flow.globals) : []
    const mappedShapes = shapes?.[0]?.shape
        ? (shapes[0].shape?.items ? Object.keys(shapes[0].shape.items.properties) : Object.keys(shapes[0].shape.properties))
        : []

    const globalsKeywords: Completion[] = globalsShape.length > 0
        ? globalsShape.map(x => ({
            label: `globals.${x}`,
            type: 'fieldInput',
            info: '-> Field Input',
            apply: `@{globals.${x}}`
        }))
        : []

    const fieldKeywords: Completion[] = mappedShapes
        ? mappedShapes?.map(x => ({
            label: x,
            type: 'fieldInput',
            info: '-> Field Input',
            apply: `@{${x}}`
        }))
        : []

    const myCompletions = (context: CompletionContext) => {
        const word = context.matchBefore(/\w*/)
        if (word.from === word.to && !context.explicit) return null
        return {
            from: word.from,
            options: [...fieldKeywords, ...globalsKeywords]
        }
    }

    return (
        <Stack spacing={3}>
            <Typography variant='h6'>Build the expressions</Typography>
            <Stack flexDirection='row' gap={1} alignItems='center'>
                <Stack flexDirection='row' gap={1} flex={0.4} alignItems='center'>
                    {renderInfoField('The name of the output column where the result of the expression will be stored. No white spaces are allowed')}
                    <Typography variant='subtitle2'>Output column:</Typography>
                </Stack>
                <Stack flexDirection='row' visibility='hidden'>
                    <Typography>=</Typography>
                </Stack>
                <Stack flexDirection='row' gap={1} flex={1} alignItems='center'>
                    {renderInfoField(
                        <span>
                            The expression used to calculate the value for the output column. Syntax:
                            <br />
                            for numbers use @&#123;&#125; for column names, round brackets for nested operation and operators (+, -, *, /);
                            <br />
                            for strings use @&#123;&#125; for column names, operator (+) for concatenation and single quotes or double quotes for the concatenated string.
                        </span>
                    )}
                    <Typography variant='subtitle2'>Expression:</Typography>
                </Stack>
                <Stack flexDirection='row'>
                    <IconButton
                        color='error'>
                        <DeleteRounded sx={{ visibility: 'hidden' }} />
                    </IconButton>
                </Stack>
            </Stack>
            {selectedNode.data?.operationsData?.expressions.map((expr, index) => (
                <Stack key={index} flexDirection='row' gap={1} alignItems='center'>
                    <Stack flexDirection='row' gap={1} flex={0.4}>
                        <TextField
                            sx={{ '& .MuiInputBase-input::placeholder': { fontSize: 12 } }}
                            fullWidth
                            name='output'
                            size='small'
                            value={expr.output ?? ''}
                            placeholder='e.g. result_column'
                            disabled={disable}
                            error={expr.output === ''}
                            onChange={event => handleInputChange(event, index)}
                        />
                    </Stack>
                    <Stack flexDirection='row'>
                        <Typography>=</Typography>
                    </Stack>
                    <Stack flexDirection='row' gap={1} flex={1} overflow='hidden'>
                        <NodeCodemirrorTextEditor
                            error={expr.expression === ''}
                            value={expr.expression ?? ''}
                            disabled={disable}
                            placeholder='e.g. (@{col1} + 10) / @{col2} or @{col1} + "text" + @{col2}'
                            style={{ overflow: 'hidden', minWidth: '100%' }}
                            onChange={event => handleInputChange(event, index)}
                            extensions={[autocompletion({ override: [myCompletions] })]}
                        />
                    </Stack>
                    <Stack flexDirection='row'>
                        <IconButton
                            color='error'
                            disabled={disable || selectedNode.data.operationsData.expressions.length === 1}
                            onClick={() => {
                                setSelected({
                                    ...selectedNode,
                                    data: {
                                        ...selectedNode.data,
                                        operationsData: {
                                            ...selectedNode.data.operationsData,
                                            expressions: selectedNode.data.operationsData.expressions.filter((_, i) => i !== index)
                                        }
                                    }
                                })
                            }}>
                            <DeleteRounded />
                        </IconButton>
                    </Stack>
                </Stack>
            ))}
            <Button
                variant='text'
                disabled={disable || selectedNode.data.operationsData?.expressions.some(expr => expr.expression === '' || expr.output === '')}
                onClick={() => {
                    setSelected({
                        ...selectedNode,
                        data: {
                            ...selectedNode.data,
                            operationsData: {
                                ...selectedNode.data.operationsData,
                                expressions: [...selectedNode.data.operationsData.expressions, { expression: '', output: '' }]
                            }
                        }
                    })
                }}>
                add new expression
            </Button>
        </Stack>
    )
}

export default memo(NodeOperationsEditor)
